import axios from 'axios'
import JSZip from 'jszip'

/**
 * Downloads a GitHub Actions artifact, extracts nested ZIP, and returns summary JSON.
 * @param artifactId {string} The ID of the artifact to download.
 * @returns {Promise<object>} The parsed summary.json data or an error.
 */
export default async function downloadArtifact(artifactId: string) {
  const { githubActionToken } = useRuntimeConfig().public
  const token = githubActionToken

  if (!token) {
    return {
      success: false,
      message: 'GITHUB_ACTION_TOKEN is not set in the .env file.',
    }
  }

  const url = `https://api.github.com/repos/hungryhub-team/hhvendor-automation/actions/artifacts/${artifactId}/zip`

  try {
    // Step 1: Download the artifact zip
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
      responseType: 'arraybuffer',
    })

    // Step 2: Load outer zip (artifact.zip)
    const artifactZip = await JSZip.loadAsync(response.data)

    // Step 3: Find and extract allure-report.zip
    const allureFile = Object.values(artifactZip.files).find(file => file.name.endsWith('allure-report.zip'))

    if (!allureFile) {
      return {
        success: false,
        message: 'allure-report.zip not found inside the artifact.',
      }
    }

    const allureZipData = await allureFile.async('arraybuffer')
    const allureZip = await JSZip.loadAsync(allureZipData)
    // Step 4: Find and extract widgets/summary.json
    const summaryFile = allureZip.file('allure-report/widgets/summary.json')
    if (!summaryFile) {
      return {
        success: false,
        message: 'summary.json not found in allure-report.zip',
      }
    }

    const summaryJson = await summaryFile.async('string')
    const parsedData = JSON.parse(summaryJson)

    return {
      success: true,
      data: parsedData,
    }
  } catch (error) {
    const errResponse = (error as any).response
    return {
      success: false,
      message: errResponse?.data?.message || (error as Error).message || 'An unknown error occurred.',
    }
  }
}
